import Image from '@/components/atoms/Image';
import Button, { ButtonVariants } from '@/components/atoms/legacy/Button';
import { InfoDisplay } from '@/components/molecules/InfoDisplay';
import { NoImageWrapper } from '@/components/molecules/NoImageWrapper/NoImageWrapper';
import { useZipcode } from '@/hooks/useZipcode';
import { BUILD_PRICE } from '@/lib/constants';
import { priceFormatter } from '@/lib/priceFormatter';
import { TrimStyleWithSpecs } from '@/lib/schema/build/types';
import { RawValueWithUnit } from '@/types';
import { ChevronUpIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { ArrowUpRightIcon } from '@heroicons/react/24/solid';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import styles from './styles.module.scss';

interface TrimComparisonResultProps {
  trims: TrimStyleWithSpecs[];
  clearTrim: (trimId: number) => void;
  containerId: string;
  scrollToTopList: () => void;
}

export const TrimComparisonResult: React.FC<TrimComparisonResultProps> = ({
  trims,
  clearTrim,
  containerId,
  scrollToTopList,
}) => {
  const { zipcode } = useZipcode();
  const router = useRouter();

  const goToInventory = async ({
    make,
    model,
    year,
  }: {
    make: string;
    model: string;
    year: string;
  }) => {
    await router.push(`/search/year_start/${year}/make/${make}/model/${model}`);
  };

  const listToDisplay = useMemo(() => {
    const result = trims.map((item) => {
      const {
        year,
        model,
        trimId,
        image,
        specs: { rating, msrp, highlights },
        range,
        trim: trimName,
        make,
      } = item;
      return {
        trimId,
        general: {
          trimName,
          make,
          image,
          year,
          model,
        },
        Rating: {
          'Safety rating': rating?.familyFriendlyRating,
        },
        Finance: {
          'Est. MSRP': priceFormatter(msrp || undefined),
        },
        'Drive Train': {
          'Drive type': highlights.Drive_Type,
          Range: range,
          Horsepower: {
            value: highlights.Horsepower.value,
            unit: highlights.Horsepower.unit,
          },
          Torque: {
            value: highlights.Torque.value,
            unit: highlights.Torque.unit,
          },
        },
        Charging: {
          'DC Charging time': highlights.DC_Charging_Time,
        },
        'Cabin capacity': {
          'Body type': highlights.Body_Type,
          'Max seating': highlights.Seating_Capacity,
          'Cargo capacity': highlights.Cargo_Capacity,
        },
      };
    });
    return result;
  }, [trims]);

  // TODO: Fix types so that you get a uniform shape and this function is not needed
  const getDefaultDisplayValues = (
    value:
      | string
      | number
      | null
      | undefined
      | { value?: string | number; unit?: string }
      | RawValueWithUnit
  ) => {
    let displayValue = 'n/a';
    let displayUnit: string | undefined;

    if (value) {
      if (typeof value === 'string' || typeof value === 'number') {
        displayValue = String(value);
      } else if ('Value' in value) {
        displayValue = value?.Value ? String(value?.Value) : 'n/a';
        displayUnit = value?.Unit;
      } else {
        displayValue = value?.value ? String(value.value) : 'n/a';
        displayUnit = value.unit;
      }
    }
    return { displayUnit, displayValue };
  };

  return (
    <div className="flex flex-col" id={containerId}>
      <div className="py-5 text-center lg:py-10">
        <span className="text-subtitle16px text-neutral-600">
          Select and compare up to two trims
        </span>
      </div>
      <div className="flex w-full flex-col justify-between bg-neutral-100 p-5 lg:p-10">
        <div className={styles.row}>
          {[listToDisplay[0], listToDisplay[1]].map((item, index) => {
            return (
              <div className={styles.subSection} key={index}>
                {item ? (
                  <>
                    <div className="flex flex-row">
                      <div
                        role="button"
                        className="cursor-pointer"
                        onClick={() => clearTrim(item.trimId)}
                      >
                        <XCircleIcon height={20} width={20} />
                      </div>
                    </div>
                    {item.general.image ? (
                      <Image
                        src={item?.general?.image}
                        key={item.general.image}
                        alt={`${item.general.trimName} image`}
                        className="self-center"
                        width={246}
                        height={246}
                      />
                    ) : (
                      <NoImageWrapper
                        make={item.general.make}
                        className={`text-blue-dark opacity-40`}
                      />
                    )}
                    <div className="flex flex-col gap-2">
                      <span className="text-subtitle16px text-neutral-600">
                        {item.general.make}
                      </span>
                      <span className="text-title24px">
                        {item.general.trimName}
                      </span>
                    </div>
                  </>
                ) : (
                  <div
                    className="mt-[20px] flex flex-1 flex-col items-center justify-center"
                    role="button"
                    onClick={scrollToTopList}
                  >
                    <div className="mb-5 max-w-[40px] bg-neutral-100 p-[10px]">
                      <ChevronUpIcon className="size-xl text-neutral-600" />
                    </div>
                    <div>
                      <span className="text-subtitle16px font-light text-neutral-600">
                        Select from above
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className={styles.section}>
          <span className={styles.sectionHeader}>Rating</span>
          <div className={styles.row}>
            {listToDisplay.map(({ Rating }, index) => {
              return (
                <div className={styles.subSection} key={index}>
                  {Object.entries(Rating).map(([key, value], i) => {
                    return (
                      <div key={i}>
                        <span className={styles.rowSubTitle}>{key}</span>
                        <InfoDisplay
                          main={{
                            text: String(value),
                            postFix: '/5 stars',
                            className: 'text-title20px text-green-medium',
                            postFixClassName: 'text-subtitle14px mt-[4px]',
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>

        <div className={styles.section}>
          <span className={styles.sectionHeader}>Finance</span>
          <div className={styles.row}>
            {listToDisplay.map(({ Finance }, index) => {
              return (
                <div className={styles.subSection} key={index}>
                  {Object.entries(Finance).map(([key, value], i) => {
                    return (
                      <div key={i}>
                        <span className={styles.rowSubTitle}>{key}</span>
                        <InfoDisplay
                          main={{
                            text: value,
                            className: 'text-title20px',
                            postFixClassName: 'text-subtitle14px mt-[4px]',
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
        <div className={styles.section}>
          <span className={styles.sectionHeader}>Drive train</span>
          <div className={styles.row}>
            {listToDisplay.map(({ 'Drive Train': Drivetrain }, index) => {
              return (
                <div className={styles.subSection} key={index}>
                  {Object.entries(Drivetrain).map(([key, value], i) => {
                    const { displayUnit, displayValue } =
                      getDefaultDisplayValues(value);
                    return (
                      <div key={i}>
                        <span className={styles.rowSubTitle}>{key}</span>
                        <InfoDisplay
                          main={{
                            text: displayValue,
                            postFix: displayUnit,
                            className: 'text-title20px',
                            postFixClassName: 'text-subtitle14px mt-[4px]',
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
        <div className={styles.section}>
          <span className={styles.sectionHeader}>Charging</span>
          <div className={styles.row}>
            {listToDisplay.map(({ Charging }, index) => {
              return (
                <div className={styles.subSection} key={index}>
                  {Object.entries(Charging).map(([key, value], i) => {
                    const { displayUnit, displayValue } =
                      getDefaultDisplayValues(value);
                    return (
                      <div key={i}>
                        <span className={styles.rowSubTitle}>{key}</span>
                        <InfoDisplay
                          main={{
                            text: displayValue,
                            postFix: displayUnit,
                            className: 'text-title20px',
                            postFixClassName: 'text-subtitle14px mt-[4px]',
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
        <div className={styles.section}>
          <span className={styles.sectionHeader}>Cabin capacity</span>
          <div className={styles.row}>
            {listToDisplay.map(({ 'Cabin capacity': cabinCapacity }, index) => {
              return (
                <div className={styles.subSection} key={index}>
                  {Object.entries(cabinCapacity).map(([key, value], i) => {
                    const { displayUnit, displayValue } =
                      getDefaultDisplayValues(value);
                    return (
                      <div key={i}>
                        <span className={styles.rowSubTitle}>{key}</span>
                        <InfoDisplay
                          main={{
                            text: displayValue,
                            postFix: displayUnit,
                            className: 'text-title20px',
                            postFixClassName: 'text-subtitle14px mt-[4px]',
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>

        <div className={styles.section}>
          <div className={`${styles.row} gap-8`}>
            {listToDisplay.map(
              ({ general: { image, trimName, make, year, model } }, index) => {
                return (
                  <div
                    key={index}
                    className={`${
                      listToDisplay.length > 1 ? styles.subSection : ''
                    } flex flex-col justify-between gap-4 ${
                      listToDisplay.length === 1 ? 'w-1/2' : ''
                    }`}
                  >
                    <div className="flex flex-col">
                      {image && (
                        <Image
                          src={image}
                          key={image}
                          alt={`${trimName} image`}
                          width={246}
                          height={246}
                        />
                      )}
                      <div className="flex flex-col gap-2">
                        <span className="text-subtitle16px text-neutral-600">
                          {make}
                        </span>
                        <span className="text-title2">{trimName}</span>
                      </div>
                    </div>
                    <div className="flex flex-col gap-5">
                      <Button
                        variant={ButtonVariants.newPrimary}
                        onClick={async () => {
                          router.push({
                            pathname: '/build/[zipcode]/[make]/[year]/[model]',
                            query: {
                              zipcode,
                              make,
                              model,
                              year,
                            },
                          });
                        }}
                        className={'w-full'}
                      >
                        {BUILD_PRICE}
                      </Button>
                      <div
                        role="button"
                        className="flex cursor-pointer flex-row items-center self-center px-4"
                        onClick={async () => {
                          await goToInventory({
                            make,
                            model,
                            year: String(year),
                          });
                        }}
                      >
                        <div className="border-b-4 border-brandElectric">
                          <span>View inventory</span>
                        </div>
                        <div className="mb-2">
                          <ArrowUpRightIcon
                            stroke="#010044"
                            width={16}
                            height={16}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
